<script setup>
import { ref, computed, onMounted } from 'vue'
import { planningApi } from '@/apis/planning.api'
import { useWebSiteStore } from '@/stores/web-site.store'

const webSiteStore = useWebSiteStore()
const data = ref(null)

onMounted(async () => {
  const res = await planningApi.getRevenueTargetSummaryAsync()
  data.value = res.data
})

const hasTarget = computed(() => data.value?.target != null)
const pct = computed(() => data.value?.pct ?? 0)
const paceStatus = computed(() => data.value?.pace_status ?? null)

const currencySymbol = computed(() => webSiteStore.getDefaultCurrency()?.symbol ?? '')

const fmt = (val) => {
  if (val == null) return '—'
  return `${currencySymbol.value}${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(val)}`
}

// Blue bar: 0–100% of target. Capped at the proportion of target within total revenue when exceeding.
const bluePct = computed(() => {
  const p = pct.value
  if (p <= 100) return p
  return (100 / p) * 100  // blue portion shrinks proportionally to make room for green
})

const greenPct = computed(() => {
  const p = pct.value
  if (p <= 100) return 0
  return ((p - 100) / p) * 100
})

const paceColor = computed(() => {
  if (paceStatus.value === 'ahead') return 'positive'
  if (paceStatus.value === 'behind') return 'negative'
  return 'primary'
})

const paceText = computed(() => {
  if (pct.value >= 100) return 'Congratulations! Yearly revenue target already met, keep going!'
  if (paceStatus.value === 'ahead') return 'Ahead of pace to hit the target early.'
  if (paceStatus.value === 'behind') return 'Behind pace — revenue is below expected.'
  return 'On track to hit the yearly target.'
})

const pctColor = computed(() => pct.value > 100 ? 'positive' : 'primary')
</script>

<template>
  <div class="fit column justify-center q-pa-md">
    <div v-if="data === null" class="col flex flex-center">
      <q-spinner size="md" color="primary" />
    </div>
    <template v-else-if="!hasTarget">
      <div class="flex flex-center column text-grey-5" style="height:100%">
        <q-icon name="mdi-target" size="2rem" class="q-mb-sm" />
        <div class="text-caption text-center">No yearly target set.<br>Configure it in company settings.</div>
      </div>
    </template>
    <template v-else>
      <!-- KPI -->
      <div :class="`text-h3 text-weight-bold text-${pctColor}`">{{ pct }}%</div>
      <div class="text-caption text-grey-7 q-mb-md">of yearly revenue target</div>

      <!-- Progress bar -->
      <div style="height: 16px; border-radius: 99px; background: #E0E0E0; position: relative; overflow: hidden;" class="q-mb-sm">
        <div
          :style="{ width: bluePct + '%' }"
          style="position: absolute; left: 0; top: 0; height: 100%; background: #1976D2; border-radius: 99px 0 0 99px;"
        />
        <div
          v-if="greenPct > 0"
          :style="{ width: greenPct + '%' }"
          style="position: absolute; right: 0; top: 0; height: 100%; background: #388E3C; border-radius: 0 99px 99px 0;"
        />
      </div>

      <!-- Amounts -->
      <div class="text-caption text-grey-7 q-mb-md">
        {{ fmt(data.ytd_revenue) }} / {{ fmt(data.target) }}
      </div>

      <!-- Pace label -->
      <div v-if="paceStatus" :class="`text-caption text-${paceColor}`" style="font-style: italic">
        {{ paceText }}
      </div>
    </template>
  </div>
</template>
